import React, { useMemo, useCallback, useRef, useEffect } from 'react'
import styled from 'styled-components'
import VimeoVideo from 'components/Common/Video/VimeoVideo'
import clsx from 'clsx'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { useWrappedStorySelector, useWrappedStoryDispatch, WrappedStoryActions } from 'contexts/Wrapped'

const Container = styled.div`
  position: absolute;
  inset: 0;
  background-color: ${props => props.theme.palette.neutral.default.one};
  overflow: hidden;
`

const StyledVimeoVideo = styled(VimeoVideo)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  pointer-events: none;
  width: 100vw;
  height: 100vh;
  /* Default: 9:16 portrait video - cover full screen with cropping */
  min-width: 56.25vh; /* 9/16 of viewport height */
  min-height: 177.78vw; /* 16/9 of viewport width */

  /* 16:9 landscape video */
  .horizontal & {
    min-width: 177.78vh; /* 16/9 of viewport height */
    min-height: 56.25vw; /* 9/16 of viewport width */
  }

  ${mediaQueryUp.tablet} {
    width: 100%;
    height: 100%;
    min-width: 56.25%; /* 9:16 default for modal */
    min-height: 177.78%;
    /* Full-page mode - use viewport units for accurate coverage */
    .horizontal & {
      width: 100vw;
      height: 100vh;
      min-width: 177.78vh; /* 16/9 of viewport height */
      min-height: 56.25vw; /* 9/16 of viewport width */
    }
  }

  iframe {
    width: 100%;
    height: 100%;
  }
`

interface VimeoVideoInstance {
  play: (source?: string) => void
  pause: () => void
}

interface Props {
  video: {
    id: string
    aspectRatio: '16:9' | '9:16'
  },
  /** Whether this slide is currently active/visible */
  isActive?: boolean
  loop?: boolean
  className?: string
}

function WrappedVideoBackground({ video, isActive = true, loop = true, className }: Props) {
  // Use granular selector to minimize re-renders
  const isPaused = useWrappedStorySelector(state => state.isPaused)
  const dispatch = useWrappedStoryDispatch()
  const videoRef = useRef<VimeoVideoInstance | null>(null)
  const isPausedRef = useRef(isPaused)
  const isActiveRef = useRef(isActive)
  // Track both playing and buffering states to determine when video is truly ready
  const hasStartedPlayingRef = useRef(false)
  const isBufferingRef = useRef(false)
  isPausedRef.current = isPaused
  isActiveRef.current = isActive

  const query = useMemo(() => ({
    controls: false,
    muted: true,
    autoplay: true,
    loop,
    autopause: false,
    // iOS compatibility
    playsinline: true,
    background: true,
  }), [loop])

  // Helper to update video ready state based on both playing and buffering status
  const updateVideoReadyState = useCallback(() => {
    if (!isActiveRef.current) return
    // Video is ready only when it has started playing AND is not buffering
    const isReady = hasStartedPlayingRef.current && !isBufferingRef.current
    dispatch({ type: WrappedStoryActions.setVideoReady, isVideoReady: isReady })
  }, [dispatch])

  const handlePlay = useCallback((component: VimeoVideoInstance) => {
    // Store the reference to the VimeoVideo instance
    videoRef.current = component
    hasStartedPlayingRef.current = true

    // Update ready state - will only be true if not currently buffering
    updateVideoReadyState()

    // If story is paused or this slide is not active, pause the video immediately
    if (isPausedRef.current || !isActiveRef.current) {
      component.pause()
    }
  }, [updateVideoReadyState])

  const handleBuffering = useCallback((isBuffering: boolean) => {
    isBufferingRef.current = isBuffering
    // Update ready state based on current playing + buffering status
    updateVideoReadyState()
  }, [updateVideoReadyState])

  // Control video playback based on isPaused and isActive state
  useEffect(() => {
    if (!videoRef.current) return

    if (!isActive || isPaused) {
      videoRef.current.pause()
    } else {
      videoRef.current.play('')
    }
  }, [isPaused, isActive])

  if (!video) {
    return <Container className={className} />
  }

  return (
    <Container className={clsx(className, { horizontal: video.aspectRatio === '16:9' })}>
      <StyledVimeoVideo
        vimeoVideoId={video.id}
        query={query}
        onPlay={handlePlay}
        onBuffering={handleBuffering}
        hideWhenBuffering
      />
    </Container>
  )
}

export default React.memo(WrappedVideoBackground)
